'use client';

import Script from 'next/script';
import { useEffect, type FC } from 'react';
import { useConfig } from '@/react/hooks/useConfig';

declare global {
  // eslint-disable-next-line jsdoc/require-jsdoc -- Please stop.
  interface Window {
    /** A temporary object to hack around Osano's script loading behavior. */
    __osanoHacks?: {
      /** A list of scripts and their parents that were deferred from being added to the DOM. */
      deferredScripts: Array<[parent: Node, HTMLScriptElement]>;
      /** The original `Node.prototype.appendChild` method. */
      nativeAppendChild: typeof Node.prototype.appendChild;
      /** The patched `Node.prototype.appendChild` method. */
      appendChild: typeof Node.prototype.appendChild;
    };
  }
}

/**
 * A script to monkey patch `appendChild` to check if the url ends with "osano-ui.js",
 * and if so, defer appending it until after hydration (in a useEffect). This is necessary
 * to prevent a hydration mismatch due to the script adding DOM nodes before React hydrates.
 * It is wrapped in an IIFE to avoid polluting the global scope more than necessary.
 */
const osanoHackScript = /* js */ `
(() => {
  const nativeAppendChild = Node.prototype.appendChild;
  const __osanoHacks = self.__osanoHacks = {
    deferredScripts: [],
    nativeAppendChild,
    appendChild: function(child) {
      if (child?.src?.endsWith('osano-ui.js')) {
        __osanoHacks.deferredScripts.push([this, child]);
        return child;
      } else {
        return nativeAppendChild.call(this, child);
      }
    }
  };
  Node.prototype.appendChild = function(child) {
    return __osanoHacks.appendChild.call(this, child);
  }
})();
`;

/** The Osano integration code script. */
export const OsanoScript: FC = () => {
  const config = useConfig('osano');
  const customerID = config.getSetting('customerID').value;
  const configurationID = config.getSetting('configurationID').value;
  const enabled = config.getSetting('enabled').value;

  useEffect(
    function appendDeferredOsanoScripts() {
      const { __osanoHacks } = window;
      if (!enabled || !__osanoHacks) return;

      /**
       * **Important Note**: We cannot simply restore the native `appendChild` method
       * onto `Node.prototype` because another script may have also patched it with
       * some additional functionality after us.
       */
      __osanoHacks.appendChild = __osanoHacks.nativeAppendChild; // restore original method
      for (const [parent, script] of __osanoHacks.deferredScripts.splice(0)) {
        parent.appendChild(script); // append deferred scripts now
      }
      delete window.__osanoHacks; // remove from global scope
      // DON'T remove the script tag as it can break React rendering
    },
    [enabled]
  );

  return enabled ? (
    <>
      {/* Note: we use the native script tag to ensure it executes as early as possible.
          "beforeInteractive" scripts act more like "defer" scripts. */}
      <script>{osanoHackScript}</script>
      <Script
        strategy="beforeInteractive"
        src={`https://cmp.osano.com/${customerID}/${configurationID}/osano.js`}
      />
    </>
  ) : null;
};
